import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  AudioLines,
  AudioLinesOff,
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  Volume2,
  VolumeX,
} from "lucide-react";
import { loadAssets, type AssetMap } from "@/game/assets";
import { attachInput, setTouchSteer, setTouchTuck } from "@/game/input";
import { unlockAudio, setMuted, isMuted } from "@/game/audio";
import { isMusicOn, setMusicOn } from "@/game/music";
import { wireControlsTest } from "@/game/controlsTest";
import {
  CRUISE_SPEED,
  FIXED_DT,
  MAX_FRAME_DT,
  MAX_SPEED,
  MIN_SPEED,
  TUCK_HINT_KEY,
  TUCK_MAX,
} from "@/game/constants";
import { createView } from "@/game/render";
import { flushOutbox } from "@/game/leaderboards";
import { ResultsCard } from "./ResultsCard";
import {
  createSim,
  hudSnapshot,
  retryRace,
  startRace,
  stepSim,
  type GameSim,
  type HudSnapshot,
} from "@/game/sim";

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable
  );
}

function formatTime(t: number): string {
  const n = Number.isFinite(t) && t > 0 ? t : 0;
  const m = Math.floor(n / 60);
  const s = n - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, "0")}`;
}

function bindSteer(dir: -1 | 1) {
  return {
    onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {}
      setTouchSteer(dir);
    },
    onPointerUp: () => setTouchSteer(0),
    onPointerCancel: () => setTouchSteer(0),
    onLostPointerCapture: () => setTouchSteer(0),
  };
}

function bindTuck() {
  return {
    onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {}
      setTouchTuck(true);
    },
    onPointerUp: () => setTouchTuck(false),
    onPointerCancel: () => setTouchTuck(false),
    onLostPointerCapture: () => setTouchTuck(false),
  };
}

// The one-time tuck hint's state machine, stepped from the frame loop beside
// the HUD snapshot. Title copy teaches the control, but copy is easy to ride
// straight past: a first-timer who has cruised untucked for a few clean
// seconds gets one nudge, then never again -- and a player who tucks
// unprompted has proven they never needed it.
type TuckHint = {
  eligibleSince: number;
  shownAt: number;
  visible: boolean;
  done: boolean;
  checked: boolean;
};

function freshTuckHint(): TuckHint {
  return { eligibleSince: 0, shownAt: 0, visible: false, done: false, checked: false };
}

function stepTuckHint(snap: HudSnapshot, h: TuckHint, show: (v: boolean) => void): void {
  if (h.done) return;
  const seen = () => {
    h.done = true;
    if (h.visible) {
      h.visible = false;
      show(false);
    }
    try {
      localStorage.setItem(TUCK_HINT_KEY, "1");
    } catch {}
  };
  if (!h.checked) {
    h.checked = true;
    try {
      if (localStorage.getItem(TUCK_HINT_KEY)) {
        h.done = true;
        return;
      }
    } catch {}
  }
  if (snap.phase !== "playing") {
    h.eligibleSince = 0;
    if (h.visible) {
      h.visible = false;
      show(false);
    }
    return;
  }
  if (snap.tuck) {
    // Held into the descent proper, this is a player who knows the control.
    // Held for a split second at the line, it is the Space that started the
    // race still coming back up -- that proves nothing, so mark nothing.
    if (snap.progress > 0.005) seen();
    return;
  }
  const now = performance.now();
  if (h.visible) {
    if (now - h.shownAt > 6000) seen();
    return;
  }
  // Airborne holds the clock rather than resetting it: a ramp is progress,
  // not evidence the player needed re-teaching. A stun or a real slowdown
  // starts the wait over.
  if (snap.air) return;
  const cruising = !snap.stun && snap.speed > CRUISE_SPEED - 60 && snap.progress > 0.01;
  if (!cruising) {
    h.eligibleSince = 0;
    return;
  }
  if (h.eligibleSince === 0) h.eligibleSince = now;
  else if (now - h.eligibleSince > 4000) {
    h.shownAt = now;
    h.visible = true;
    show(true);
  }
}

export function Game() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simRef = useRef<GameSim | null>(null);
  const assetsRef = useRef<AssetMap | null>(null);
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState<"title" | "playing" | "results">("title");
  const [hud, setHud] = useState<HudSnapshot>({
    time: 0,
    speed: 0,
    air: false,
    stun: false,
    tuck: false,
    progress: 0,
    phase: "title",
    best: 0,
    isBest: false,
    hotDogs: 0,
    burgers: 0,
    adjustedMs: 0,
    seed: 0,
  });
  // Seeded from the persisted preference: a player who muted the arcade last
  // visit stays muted this one.
  const [muted, setMutedUi] = useState(isMuted);
  // Its own preference, kept apart from the mute: the sound effects are the
  // physical half of the game, and a player who does not want a score should
  // not have to give them up to switch it off.
  const [music, setMusicUi] = useState(isMusicOn);
  const hudTick = useRef(0);
  const [hint, setHint] = useState(false);
  const hintRef = useRef<TuckHint>(freshTuckHint());

  useEffect(() => {
    // Anything a previous session could not post gets another go now.
    flushOutbox();
    let stop = false;
    void loadAssets().then((assets) => {
      if (stop) return;
      assetsRef.current = assets;
      setReady(true);
    });
    return () => {
      stop = true;
    };
  }, []);

  useEffect(() => {
    if (phase !== "playing") {
      setTouchSteer(0);
      setTouchTuck(false);
    }
  }, [phase]);

  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const assets = assetsRef.current;
    if (!canvas || !assets) return;

    const sim = createSim();
    simRef.current = sim;
    wireControlsTest(sim);
    const detach = attachInput(canvas);
    const view = createView(canvas, assets);

    let acc = 0;
    let last = performance.now();
    let raf = 0;

    const loop = (now: number) => {
      const dt = Math.min((now - last) / 1000, MAX_FRAME_DT);
      last = now;
      acc += dt;
      while (acc >= FIXED_DT) {
        stepSim(sim, FIXED_DT);
        acc -= FIXED_DT;
      }
      view.render(sim, acc / FIXED_DT);
      hudTick.current += 1;
      if (hudTick.current % 2 === 0) {
        const snap = hudSnapshot(sim);
        setHud(snap);
        setPhase(snap.phase);
        stepTuckHint(snap, hintRef.current, setHint);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      detach();
      view.dispose();
    };
  }, [ready]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      // The results card holds a real text field for initials. A key typed
      // into it is the player's, not a shortcut: without this, R restarts
      // the run and Enter restarts instead of posting.
      if (isTyping(e.target)) return;
      const sim = simRef.current;
      if (!sim) return;
      if ((e.code === "Enter" || e.code === "Space") && sim.phase === "title") {
        e.preventDefault();
        begin();
      }
      if (
        (e.code === "KeyR" || e.code === "Enter" || e.code === "Space") &&
        sim.phase === "results"
      ) {
        e.preventDefault();
        again();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function begin() {
    const sim = simRef.current;
    if (!sim) return;
    unlockAudio();
    startRace(sim);
    setPhase("playing");
  }

  function again() {
    const sim = simRef.current;
    if (!sim) return;
    unlockAudio();
    retryRace(sim);
    setPhase("playing");
  }

  function toggleMute() {
    const next = !isMuted();
    setMuted(next);
    setMutedUi(next);
  }

  function toggleMusic() {
    const next = !isMusicOn();
    setMusicOn(next);
    setMusicUi(next);
  }

  const speedT = Math.max(0, Math.min(1, (hud.speed - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)));
  // Where the tuck plateau sits on the bar: the notch that says the bar has a
  // second act above it, reached only off the ramps.
  const tuckT = (TUCK_MAX - MIN_SPEED) / (MAX_SPEED - MIN_SPEED);
  const food = hud.hotDogs + hud.burgers;

  return (
    <div className="relative h-dvh w-full overflow-hidden bg-pine-deep text-snow">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full touch-none select-none"
        style={{ touchAction: "none" }}
        onContextMenu={(e) => e.preventDefault()}
      />

      {phase === "title" && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-end bg-gradient-to-t from-pine-deep via-pine-deep/80 to-transparent px-6 pb-16 pt-[max(3rem,env(safe-area-inset-top))]">
          <div className="flex w-full max-w-md flex-col items-center text-center">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.28em] text-ice-bright">
              Appalachian drop
            </p>
            <h1 className="font-display text-5xl font-semibold leading-none tracking-tight text-snow sm:text-6xl">
              Sharp Mountain
            </h1>
            <p className="mt-5 max-w-sm text-base leading-snug text-snow-dim">
              Ride the tube. Steer only left and right. Tuck to go faster, jump to keep it. Beat the
              mountain.
            </p>
            <button
              type="button"
              onClick={begin}
              disabled={!ready}
              className="mt-8 min-h-12 w-full max-w-xs rounded-xl bg-snow px-8 py-3 text-base font-semibold text-pine-deep transition-transform duration-150 ease-out hover:bg-ice-bright focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice-bright active:scale-[0.98] disabled:opacity-50"
            >
              {ready ? "Ride" : "Loading"}
            </button>
            <p className="mt-5 text-sm text-ice [@media(pointer:coarse)]:hidden">
              A / D or arrows to dodge · hold Space to tuck
            </p>
            <p className="mt-5 hidden text-sm text-ice [@media(pointer:coarse)]:block">
              Hold a side to steer, the middle button to tuck
            </p>
          </div>
        </div>
      )}

      {ready && phase === "playing" && (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col">
          <div className="flex items-start justify-between px-4 pr-28 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6 sm:pr-32">
            <div className="rounded-md bg-pine-deep/55 px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-ice">Speed</p>
              <p className="font-mono text-lg font-semibold tabular-nums text-snow">
                {Math.round(hud.speed)}
              </p>
              <div className="relative mt-1 h-1 w-16 overflow-hidden rounded-full bg-pine-mid">
                <div className="h-full bg-ice-bright" style={{ width: `${speedT * 100}%` }} />
                <div
                  className="absolute inset-y-0 w-px bg-snow/60"
                  style={{ left: `${tuckT * 100}%` }}
                />
              </div>
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-ice">Food</p>
              <p className="font-mono text-lg font-semibold tabular-nums text-snow">{food}</p>
            </div>
            <div className="text-center">
              <p className="font-mono text-4xl font-semibold tabular-nums tracking-tight text-snow drop-shadow-hud sm:text-5xl">
                {formatTime(hud.time)}
              </p>
              {hud.air && (
                <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-ice-bright">
                  Air
                </p>
              )}
              {hud.stun && !hud.air && (
                <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-snow-dim">
                  Stunned
                </p>
              )}
            </div>
            <div className="flex h-28 w-3 flex-col justify-end overflow-hidden rounded-full bg-pine-deep/55 p-0.5">
              <div
                className="w-full rounded-full bg-ice-bright"
                style={{ height: `${hud.progress * 100}%` }}
              />
            </div>
          </div>
          <div
            role="status"
            aria-live="polite"
            className="absolute inset-x-0 top-[42%] flex justify-center"
          >
            {hint && (
              <>
                <p className="rounded-lg bg-pine-deep/75 px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.2em] text-ice-bright motion-safe:animate-pulse [@media(pointer:coarse)]:hidden">
                  Hold Space to tuck
                </p>
                <p className="hidden rounded-lg bg-pine-deep/75 px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.2em] text-ice-bright motion-safe:animate-pulse [@media(pointer:coarse)]:block">
                  Hold the middle button to tuck
                </p>
              </>
            )}
          </div>
          <div className="pointer-events-auto mt-auto hidden gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] [@media(pointer:coarse)]:flex">
            <button
              type="button"
              aria-label="Steer left"
              className="flex h-14 flex-1 touch-none select-none items-center justify-center rounded-xl bg-pine/70 text-snow active:bg-pine"
              {...bindSteer(-1)}
            >
              <ChevronLeft className="size-7" strokeWidth={2} />
            </button>
            <button
              type="button"
              aria-label="Speed up"
              className="flex h-14 flex-1 touch-none select-none items-center justify-center rounded-xl bg-pine/70 text-snow active:bg-pine"
              {...bindTuck()}
            >
              <ChevronsDown className="size-7" strokeWidth={2} />
            </button>
            <button
              type="button"
              aria-label="Steer right"
              className="flex h-14 flex-1 touch-none select-none items-center justify-center rounded-xl bg-pine/70 text-snow active:bg-pine"
              {...bindSteer(1)}
            >
              <ChevronRight className="size-7" strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {ready && phase === "results" && <ResultsCard hud={hud} onAgain={again} />}

      <div className="absolute right-4 top-[max(0.75rem,env(safe-area-inset-top))] z-20 flex gap-2 sm:right-6">
        <button
          type="button"
          aria-label={music ? "Turn music off" : "Turn music on"}
          aria-pressed={music}
          onClick={() => {
            unlockAudio();
            toggleMusic();
          }}
          className="grid size-11 place-items-center rounded-md bg-pine/60 text-snow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice-bright"
        >
          {music ? <AudioLines className="size-5" /> : <AudioLinesOff className="size-5" />}
        </button>
        <button
          type="button"
          aria-label={muted ? "Unmute" : "Mute"}
          aria-pressed={muted}
          onClick={() => {
            unlockAudio();
            toggleMute();
          }}
          className="grid size-11 place-items-center rounded-md bg-pine/60 text-snow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice-bright"
        >
          {muted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
        </button>
      </div>
    </div>
  );
}
